import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Mail, Layers, Boxes, Image, HelpCircle, TrendingUp, ArrowUpRight, ChevronRight } from "lucide-react";
import { api, apiErr } from "./api";
import { PageHeader } from "./ui";
import { toast } from "sonner";

const FUNNEL_STEPS = [
  { key: "page_view", label: "Sayfa görüntüleme", rateKey: null },
  { key: "quote_upload_started", label: "Dosya yüklendi", rateKey: "upload_from_view" },
  { key: "quote_analysis_success", label: "Analiz tamamlandı", rateKey: "analysis_from_upload" },
  { key: "quote_submit_success", label: "Teklif gönderildi", rateKey: "submit_from_analysis" },
];

function FunnelCard({ funnel }) {
  if (!funnel) return null;
  const overall = funnel.rates?.overall ?? 0;
  return (
    <div className="border border-white/10 bg-[#0C0C0F] p-5" data-testid="funnel-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Conversion Funnel</div>
          <div className="font-display text-sm font-bold text-white">Ziyaretten teklife</div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-black text-[#FF4F00]">{overall}%</div>
          <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">genel dönüşüm</div>
        </div>
      </div>
      <div className="space-y-2">
        {FUNNEL_STEPS.map((step, i) => {
          const count = funnel[step.key] ?? 0;
          const max = funnel[FUNNEL_STEPS[0].key] || 1;
          const pct = Math.round((count / max) * 100);
          const rate = step.rateKey ? (funnel.rates?.[step.rateKey] ?? 0) : null;
          return (
            <div key={step.key} data-testid={`funnel-step-${step.key}`}>
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  {i > 0 && <ChevronRight className="w-3 h-3 text-zinc-600 shrink-0" />}
                  <span className="font-mono text-[11px] text-zinc-400 truncate">{step.label}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {rate !== null && (
                    <span className="font-mono text-[10px] text-zinc-600">{rate}%</span>
                  )}
                  <span className="font-display font-bold text-sm text-white tabular-nums">{count.toLocaleString("tr-TR")}</span>
                </div>
              </div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    background: i === 0 ? "#3f3f46" : i === FUNNEL_STEPS.length - 1 ? "#FF4F00" : "#52525b",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [funnel, setFunnel] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/admin/funnel").then((r) => setFunnel(r.data)).catch(() => {});
  }, []);

  const tiles = s ? [
    { label: "Bu hafta teklif", value: s.quotes_this_week, icon: TrendingUp, link: "/dekosta-yonetim/teklifler", testid: "stat-quotes-week" },
    { label: "Yeni teklif", value: s.new_quotes, icon: FileText, link: "/dekosta-yonetim/teklifler", testid: "stat-new-quotes" },
    { label: "Okunmamış mesaj", value: s.unread_messages, icon: Mail, link: "/dekosta-yonetim/mesajlar", testid: "stat-unread-msgs" },
    { label: "Toplam teklif", value: s.total_quotes, icon: FileText, link: "/dekosta-yonetim/teklifler", testid: "stat-total-quotes" },
    { label: "Hizmet", value: s.services, icon: Layers, link: "/dekosta-yonetim/hizmetler", testid: "stat-services" },
    { label: "Malzeme", value: s.materials, icon: Boxes, link: "/dekosta-yonetim/malzemeler", testid: "stat-materials" },
    { label: "Portföy", value: s.portfolio, icon: Image, link: "/dekosta-yonetim/portfoy", testid: "stat-portfolio" },
    { label: "SSS", value: s.faqs, icon: HelpCircle, link: "/dekosta-yonetim/faq", testid: "stat-faqs" },
  ] : [];

  return (
    <div>
      <PageHeader title="Dashboard" description="Sitenizin özet istatistikleri ve hızlı erişim." />
      {!s ? (
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Yükleniyor…</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-tiles">
            {tiles.map((t) => (
              <Link key={t.label} to={t.link} data-testid={t.testid} className="border border-white/10 bg-[#0C0C0F] p-5 hover:bg-[#141418] hover:border-[#FF4F00]/40 transition-colors group">
                <div className="flex items-center justify-between mb-6">
                  <t.icon className="w-5 h-5 text-[#FF4F00]" strokeWidth={1.5} />
                  <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-[#FF4F00] transition-colors" />
                </div>
                <div className="font-display text-3xl md:text-4xl font-black">{t.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">{t.label}</div>
              </Link>
            ))}
          </div>
          <FunnelCard funnel={funnel} />
        </div>
      )}
    </div>
  );
}
